import { createAsyncThunk } from '@reduxjs/toolkit';

import { Services } from 'services';
import { transformErrorResponse } from 'utils';

import { actionMapForDataLoaders, actionMapForOtherLoaders, actionKeys } from 'constants/index';

const fetchProjects = createAsyncThunk(actionMapForDataLoaders.fetchProjects, async (_, thunkApi) => {
  return Services.getProjects()
    .then(response => {
      return response.data.items || [];
    })
    .catch(error => {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    });
});

const fetchWhitelistedProjects = createAsyncThunk(
  actionMapForDataLoaders.fetchWhitelistedProjects,
  async (_, thunkApi) => {
    return Services.getWhitelistedProjects()
      .then(response => {
        const items: any[] = response.data || [];
        return items.map((p: any) => p.projectId);
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchProjectDetails = createAsyncThunk(
  actionMapForDataLoaders.fetchProjectDetails,
  async (query: { projectId: string }, thunkApi) => {
    return Services.getProjectLite(query.projectId)
      .then(response => {
        return response.data || {};
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchProjectSettings = createAsyncThunk(
  actionMapForDataLoaders.fetchProjectSettings,
  async (query: { projectId: string }, thunkApi) => {
    return Services.getProjectSettings(query.projectId)
      .then(response => {
        return response.data || {};
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchWorkerProfileCustomFields = createAsyncThunk(
  actionMapForDataLoaders.fetchWorkerProfileCustomFields,
  async (query: { projectId: string; workerId?: string; companyIds?: string[] }, thunkApi) => {
    return Services.getWorkerProfileCustomFields(query)
      .then(response => {
        const isProjectOnlyData = !query.workerId;
        return { data: response.data || [], isProjectOnlyData };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchWorkerProfileFieldsForAllCompanies = createAsyncThunk(
  actionMapForDataLoaders.fetchWorkerProfileFieldsForAllCompanies,
  async (query: { projectId: string; languageCode?: string; isPublic?: boolean }, thunkApi) => {
    return Services.getWorkerProfileFieldsForAllCompanies(query.projectId, query.languageCode, query.isPublic)
      .then(response => {
        return response.data || [];
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchAppLanguages = createAsyncThunk(
  actionKeys.fetchAppLanguages,
  async (query: { languageCode: string }, thunkApi) => {
    return Services.getAppLanguages(query.languageCode)
      .then(response => {
        return { [query.languageCode]: response.data || [] };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchCompanies = createAsyncThunk(
  actionKeys.fetchCompanies,
  async (query: { projectId: string; languageCode: string }, thunkApi) => {
    const state: any = thunkApi.getState();
    const { userId, isWorker } = state.user || {};
    const isPublic = !userId || isWorker;

    return Services.getCompanies(query.projectId, query.languageCode, isPublic)
      .then(response => {
        return { [query.languageCode]: response.data.items || [] };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchEthnicities = createAsyncThunk(
  actionKeys.fetchEthnicities,
  async (query: { languageCode: string }, thunkApi) => {
    return Services.getEthnicities(query.languageCode)
      .then(response => {
        return { [query.languageCode]: response.data || [] };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchLanguages = createAsyncThunk(
  actionKeys.fetchLanguages,
  async (query: { languageCode: string }, thunkApi) => {
    return Services.getLanguages(query.languageCode)
      .then(response => {
        return { [query.languageCode]: response.data || [] };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchCountries = createAsyncThunk(
  actionKeys.fetchCountries,
  async (query: { languageCode: string }, thunkApi) => {
    return Services.getCountries(query.languageCode)
      .then(response => {
        return { [query.languageCode]: response.data || [] };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchStates = createAsyncThunk(actionKeys.fetchStates, async (query: { languageCode: string }, thunkApi) => {
  return Services.getStates(query.languageCode)
    .then(response => {
      return { [query.languageCode]: response.data || [] };
    })
    .catch(error => {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    });
});

const fetchIdTypes = createAsyncThunk(actionKeys.fetchIdTypes, async (query: { languageCode: string }, thunkApi) => {
  return Services.getIDTypes(query.languageCode)
    .then(response => {
      return { [query.languageCode]: response.data || [] };
    })
    .catch(error => {
      return thunkApi.rejectWithValue(transformErrorResponse(error));
    });
});

const fetchSkilledTrades = createAsyncThunk(
  actionKeys.fetchSkilledTrades,
  async (query: { languageCode: string }, thunkApi) => {
    return Services.getSkilledTrades(query.languageCode)
      .then(response => {
        return { [query.languageCode]: response.data || [] };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchCertificationTypes = createAsyncThunk(
  actionKeys.fetchCertificationTypes,
  async (query: { languageCode: string }, thunkApi) => {
    return Services.getCertificationTypes(query.languageCode)
      .then(response => {
        return { [query.languageCode]: response.data || [] };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchConsentFormFields = createAsyncThunk(
  actionKeys.fetchConsentFormFields,
  async (query: { languageCode: string; projectId: string }, thunkApi) => {
    return Services.getConsentFormFields(query.languageCode, query.projectId)
      .then(response => {
        return { [query.languageCode]: response.data || [] };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

/**
 * Fetches the PROJECT-LEVEL consent agreement definitions (documents, kinds, sequence).
 *
 * Deliberately takes no `workerId`: the response's `isAccepted` flags are per-worker and belong in
 * the `onboarding` slice — see `fetchWorkerConsentAcceptances`. Keeping this call worker-agnostic is
 * what makes `cache.consentAgreements` safe to share across workers and to persist for offline use.
 */
const fetchConsentAgreements = createAsyncThunk(
  actionMapForDataLoaders.fetchConsentAgreements,
  async (query: { projectId?: string; languageCode?: string } = {}, thunkApi) => {
    // The API returns agreements for all languages in one call; consumers filter
    // by the active language when selecting a document.
    return Services.getConsentAgreements({
      projectId: query.projectId,
      languageCode: query.languageCode,
    })
      .then(response => {
        return response.data || [];
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchJobTitles = createAsyncThunk(
  actionKeys.fetchJobTitles,
  async (query: { languageCode: string }, thunkApi) => {
    return Services.getJobTitles(query.languageCode)
      .then(response => {
        return { [query.languageCode]: response.data || [] };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchTradeStatus = createAsyncThunk(
  actionKeys.fetchTradeStatus,
  async (query: { languageCode: string }, thunkApi) => {
    return Services.getTradeStatus(query.languageCode)
      .then(response => {
        return { [query.languageCode]: response.data || [] };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchSocJobTitles = createAsyncThunk(
  actionKeys.fetchSocJobTitles,
  async (query: { languageCode: string }, thunkApi) => {
    return Services.getSocJobTitles(query.languageCode)
      .then(response => {
        return { [query.languageCode]: response.data || [] };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchLanguageTurnerProtocols = createAsyncThunk(
  actionKeys.fetchLanguageTurnerProtocols,
  async (query: { languageCode: string }, thunkApi) => {
    return Services.getLanguageTurnerProtocol(query.languageCode)
      .then(response => {
        return { [query.languageCode]: response.data || [] };
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const declineProject = createAsyncThunk(
  actionMapForOtherLoaders.declineProject,
  async (query: { projectId: string; workerId: string; companyId: string; languageCode?: string }, thunkApi) => {
    const { projectId, workerId, companyId, languageCode = 'en' } = query;
    const data = { workerId, companyId };

    return Services.declineProject(projectId, data, languageCode)
      .then(response => {
        return response.data || {};
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

const fetchBadgePdf = createAsyncThunk(
  actionMapForOtherLoaders.fetchBadgePdf,
  async (
    query: {
      id: string;
      templateId?: string;
      languageCode?: string;
      isWorker: boolean;
    },
    thunkApi,
  ) => {
    return Services.fetchBadgePdf(query.id, query.isWorker, query.templateId, query.languageCode)
      .then(response => {
        // Convert blob to base64
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve(reader.result as string);
          };
          reader.onerror = reject;
          reader.readAsDataURL(response.data);
        });
      })
      .catch(error => {
        return thunkApi.rejectWithValue(transformErrorResponse(error));
      });
  },
);

export {
  fetchProjects,
  fetchWhitelistedProjects,
  fetchProjectDetails,
  fetchProjectSettings,
  fetchWorkerProfileCustomFields,
  fetchWorkerProfileFieldsForAllCompanies,
  fetchAppLanguages,
  fetchCompanies,
  fetchEthnicities,
  fetchLanguages,
  fetchCountries,
  fetchStates,
  fetchIdTypes,
  fetchSkilledTrades,
  fetchCertificationTypes,
  fetchConsentFormFields,
  fetchConsentAgreements,
  fetchJobTitles,
  fetchTradeStatus,
  fetchSocJobTitles,
  fetchLanguageTurnerProtocols,
  declineProject,
  fetchBadgePdf,
};
